import { useEffect } from 'react';

/**
 * Horizontal scroll rows (hub tab bar, level-2 sub-tabs):
 *  - on mount and whenever `activeKey` changes, the active tab is scrolled into view (centred; uses rects, so it
 *    is direction-agnostic: works in RTL where scrollLeft is negative);
 *  - sets data-fade-left / data-fade-right on the row's wrapper (the nearest .pp-scrollfade) while there are more
 *    tabs beyond that edge, so CSS can draw a subtle fade. Never throws (no layout in tests/SSR).
 */
export function useScrollRow(rowRef, activeKey) {
  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof row.getBoundingClientRect !== 'function') return undefined;
    const wrap = row.closest('.pp-scrollfade') || row;

    const update = () => {
      const max = row.scrollWidth - row.clientWidth;
      if (max <= 1) { wrap.dataset.fadeLeft = '0'; wrap.dataset.fadeRight = '0'; return; }
      const atMin = row.scrollLeft <= 1 && row.scrollLeft >= -1;               // scrollLeft 0 = the START edge
      const atEnd = Math.abs(row.scrollLeft) >= max - 1;
      const rtl = getComputedStyle(row).direction === 'rtl';
      // LTR: start = left; RTL: start = right (scrollLeft is 0 at the right edge and goes negative)
      const moreAtStart = !atMin;
      const moreAtEnd = !atEnd;
      wrap.dataset.fadeLeft = String(rtl ? (moreAtEnd ? 1 : 0) : (moreAtStart ? 1 : 0));
      wrap.dataset.fadeRight = String(rtl ? (moreAtStart ? 1 : 0) : (moreAtEnd ? 1 : 0));
    };

    const active = row.querySelector('[aria-current], [aria-selected="true"], .pp-chip-active, .pp-subtab-active');
    if (active) {
      const a = active.getBoundingClientRect(); const r = row.getBoundingClientRect();
      const delta = (a.left + a.width / 2) - (r.left + r.width / 2);
      if (Math.abs(delta) > 1) row.scrollLeft += delta;
    }
    update();
    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { row.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [rowRef, activeKey]);
}

export default useScrollRow;
